import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/material";
import isNil from "lodash/isNil";

import { formatAddressObjectFullAddress } from "../../../../_src_shared/utils/functions/formatAddressObjectFullAddress";
import formatDollar from "../../../../_src_shared/utils/functions/formatDollar";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import { useLoanFilesHook } from "../../../../hooks/useLoanFilesHook";
import { loanFetchedDataAuditLogsSet } from "../../../../redux/reducers/loanFetchedDataReducer";
import { SectionHeader } from "../../../../_src_shared/components/layout/PrimaryContentCard";
import FlxGrid from "../../../../_src_shared/components/layout/FlxGrid";

import {
  parseIsoMs,
  fmtDateShort,
  daysFromNow,
  firstDocByType,
} from "./cards/_shared";
import LoanActivityCardHorizontal from "./cards/LoanActivityCardHorizontal";
import LoanDatesCard from "./cards/LoanDatesCard";
import BorrowerDatesCard from "./cards/BorrowerDatesCard";
import PropertyDatesCard from "./cards/PropertyDatesCard";
import LoanTimelineCard from "./cards/LoanTimelineCard";
import CombinedTimelineCard from "./cards/CombinedTimelineCard";
import LoanContactsCard from "./cards/LoanContactsCard";
import ComplianceCard from "./cards/ComplianceCard";
import DocumentChecklistCard from "./cards/DocumentChecklistCard";

/* ───────────────── tab ───────────────── */

const LoanDrilldownKeyDatesAndDocumentsTab = () => {
  const dispatch = useDispatch();
  const loan = useSelector((state) => state.loanDrilldown);
  const auditLogs = useSelector((state) => state.loanFetchedData?.auditLogs);
  const { getLoanDocAuditLogs } = useLoanFilesHook();

  // Audit log fetch — once per loan unless slice is cleared elsewhere.
  useEffect(() => {
    if (!loan?._id) return;
    if (!isNil(auditLogs)) return;
    getLoanDocAuditLogs({
      dealId: loan._id,
      onSuccessFn: (data) => dispatch(loanFetchedDataAuditLogsSet(data)),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loan?._id, auditLogs]);

  const productType = (loan?.loanProductType || "").toLowerCase();
  const isConstruction =
    productType.includes("fix") ||
    productType.includes("flip") ||
    productType.includes("ground up") ||
    productType.includes("construction") ||
    productType.includes("rehab");
  const isDscr = productType.includes("dscr");

  /* Header meta — what shows on the top-right of the page header. Falls back
     gracefully when fields are missing. */
  // eslint-disable-next-line no-unused-vars
  const loanMeta = useMemo(() => {
    const numberOrId =
      loan?.loanNumber || loan?.loanId || loan?._id?.slice?.(-6) || null;
    const firstProperty = (loan?.subjectProperties || [])[0];
    const address = firstProperty?.address
      ? formatAddressObjectFullAddress(firstProperty.address)
      : null;
    const totalAmount =
      loan?.loanTerms?.totalLoanAmount ||
      loan?.totalLoanAmount ||
      loan?.loanAmount;
    return {
      number: numberOrId,
      address,
      productLabel: loan?.loanProductType || null,
      amount: totalAmount ? formatDollar(totalAmount) : null,
    };
  }, [loan]);

  /* Last comment — derived max of comments[].commentDateTime. */
  const lastCommentDate = useMemo(() => {
    const comments = loan?.comments || [];
    if (!comments.length) return null;
    let max = null;
    for (const c of comments) {
      const t = parseIsoMs(c?.commentDateTime);
      if (t == null) continue;
      if (max === null || t > max) max = t;
    }
    return max ? new Date(max).toISOString() : null;
  }, [loan?.comments]);

  /* Lead created — earliest of borrowers[].signUpDate / createDateTime. */
  const leadCreatedDate = useMemo(() => {
    const borrowers = loan?.borrowers || [];
    if (!borrowers.length) return null;
    let earliest = null;
    for (const b of borrowers) {
      const t = parseIsoMs(b?.signUpDate || b?.createDateTime);
      if (t == null) continue;
      if (earliest === null || t < earliest) earliest = t;
    }
    return earliest ? new Date(earliest).toISOString() : null;
  }, [loan?.borrowers]);

  /* Latest milestone status change — drives the "current stage entered"
     card. Falls back to null if no milestone history exists. */
  const latestMilestoneChange = useMemo(() => {
    const changes = loan?.milestoneStatusChanges || [];
    if (!changes.length) return null;
    let latest = null;
    for (const c of changes) {
      const t = parseIsoMs(c?.milestoneChangeDate);
      if (t == null) continue;
      if (latest === null || t > latest.t) latest = { t, change: c };
    }
    return latest?.change || null;
  }, [loan?.milestoneStatusChanges]);

  /* Pipeline progression — milestoneStatusChanges with no-op rows filtered
     out (where prior === updated). Sorted earliest first so the panel reads
     as a forward progression of stages. */
  const milestoneTransitions = useMemo(() => {
    const changes = loan?.milestoneStatusChanges || [];
    if (!changes.length) return [];
    return changes
      .map((c) => ({ ...c, ms: parseIsoMs(c?.milestoneChangeDate) }))
      .filter(
        (c) =>
          c.ms != null && c.priorMilestoneStatus !== c.updatedMilestoneStatus,
      )
      .sort((a, b) => a.ms - b.ms);
  }, [loan?.milestoneStatusChanges]);

  /* Unified timeline — single x-axis shared by milestones (top track) and
     pipeline stages (bottom track). Shape:
       { start, end, todayPct, milestones: [...], segments: [...] }
     Every position is a percentage between `start` and `end`, computed by a
     single `pctOf(ms)` so the two tracks line up automatically.
     This view is intentionally past-only: end = now, future-dated
     milestones are dropped, and the canonical pipeline isn't projected
     onto the bottom track. The current stage simply runs to now and the
     rightmost milestone is always "Today". */
  const unifiedTimeline = useMemo(() => {
    const created = parseIsoMs(loan?.createDateTime);
    const closing = parseIsoMs(loan?.expectedClosingDate);
    const firstPay = parseIsoMs(loan?.firstPaymentDate);
    const maturity = parseIsoMs(loan?.maturityDate);
    const earliestMilestone = milestoneTransitions[0]?.ms ?? null;
    const now = Date.now();

    const startCandidates = [created, earliestMilestone].filter(
      (x) => x != null,
    );
    const start = startCandidates.length
      ? Math.min(...startCandidates)
      : now - 30 * 86_400_000;
    // End at "now" — the combined view is a look-back, not a forecast.
    const end = Math.max(now, start + 1);
    const range = Math.max(end - start, 1);
    const pctOf = (t) => {
      if (t == null) return null;
      return Math.max(0, Math.min(100, ((t - start) / range) * 100));
    };

    /* Top track: loan-level milestones — but only ones that have actually
       happened. Future-dated milestones (closing not yet reached, first
       payment, maturity) are filtered out. The rightmost item is always
       "Today" so the operator sees the timeline ends at the present. */
    const closingDays =
      closing != null ? Math.round((closing - now) / 86_400_000) : null;
    const milestones = [
      created != null &&
        created <= now && {
          key: "originated",
          label: "Originated",
          date: created,
          meta: null,
          status: "done",
          leftPct: pctOf(created),
        },
      closing != null &&
        closing <= now && {
          key: "closing",
          label: "Closing",
          date: closing,
          meta:
            closingDays != null && closingDays < 0
              ? `${Math.abs(closingDays)}d overdue`
              : null,
          status: "warn",
          leftPct: pctOf(closing),
        },
      firstPay != null &&
        firstPay <= now && {
          key: "firstPayment",
          label: "First payment",
          date: firstPay,
          meta: null,
          status: "done",
          leftPct: pctOf(firstPay),
        },
      maturity != null &&
        maturity <= now && {
          key: "maturity",
          label: "Maturity",
          date: maturity,
          meta: null,
          status: "done",
          leftPct: pctOf(maturity),
        },
      // Always anchor the right edge with a Today marker.
      {
        key: "today",
        label: "Today",
        date: now,
        meta: null,
        status: "today",
        leftPct: 100,
      },
    ].filter(Boolean);

    /* Bottom track: stage segments. Past stages span their actual range;
       the current stage runs from its start to "now". No future
       projections — those would push the axis past today. Each segment
       carries the originating transition's `priorStatus` and `updateBy`
       so the waterfall rows below can render "Reached X / from Y · who"
       without re-joining against milestoneTransitions. */
    const segments = [];
    if (milestoneTransitions.length > 0) {
      const firstT = milestoneTransitions[0];
      if (firstT.priorMilestoneStatus) {
        segments.push({
          key: `seg-start-${firstT.priorMilestoneStatus}`,
          name: firstT.priorMilestoneStatus,
          startMs: start,
          endMs: firstT.ms,
          status: "done",
          isStartState: true,
          priorStatus: null,
          updateBy: null,
        });
      }
      for (let i = 0; i < milestoneTransitions.length; i += 1) {
        const t = milestoneTransitions[i];
        const nextT = milestoneTransitions[i + 1];
        const isCurrent = !nextT;
        segments.push({
          key: `seg-${t.updatedMilestoneStatus}-${i}`,
          name: t.updatedMilestoneStatus,
          startMs: t.ms,
          endMs: nextT ? nextT.ms : end,
          status: isCurrent ? "current" : "done",
          isStartState: false,
          priorStatus: t.priorMilestoneStatus || null,
          updateBy: t.updateBy || null,
        });
      }
    }

    /* Compute the true axis-based positions, then redistribute so every
       segment meets a minimum visible width. Bumping a single segment
       past its true width and rescaling the row back to 100% causes a
       slight x-axis compression of wider stages, but it eliminates the
       block-overlap that CSS minWidth was producing. The rightmost
       endpoint stays at 100% so the Today milestone still aligns. */
    const trueSegments = segments
      .map((s) => {
        const left = pctOf(s.startMs);
        const right = pctOf(s.endMs);
        return {
          ...s,
          trueLeftPct: left,
          trueWidthPct: Math.max(0, right - left),
        };
      })
      .filter((s) => s.trueWidthPct > 0);

    // ~80px visual floor at typical container widths (~1200px). Bumped
    // up so brief stages (1-day transitions) keep the stage label
    // legible — at 3% they were truncating to a single character.
    const MIN_SEG_WIDTH_PCT = 6.5;
    const bumpedTotal = trueSegments.reduce(
      (acc, s) => acc + Math.max(s.trueWidthPct, MIN_SEG_WIDTH_PCT),
      0,
    );
    const scaleFactor = bumpedTotal > 0 ? 100 / bumpedTotal : 1;
    let runningLeft = 0;
    const balancedSegments = trueSegments.map((s) => {
      const w = Math.max(s.trueWidthPct, MIN_SEG_WIDTH_PCT) * scaleFactor;
      const result = {
        ...s,
        leftPct: runningLeft,
        widthPct: w,
      };
      runningLeft += w;
      return result;
    });

    return {
      start,
      end,
      todayPct: pctOf(now),
      milestones,
      segments: balancedSegments,
      hasData: milestones.length > 0 || balancedSegments.length > 0,
    };
  }, [loan, milestoneTransitions]);

  /* Timeline anchors — origination, today, expected close, first payment.
     Marker positions are computed proportionally between the earliest and
     latest known timestamps so the visual track always uses its full width.
     The progress fill stops at "today" within that range. */
  const timeline = useMemo(() => {
    const created = parseIsoMs(loan?.createDateTime);
    const closing = parseIsoMs(loan?.expectedClosingDate);
    const firstPay = parseIsoMs(loan?.firstPaymentDate);
    const maturity = parseIsoMs(loan?.maturityDate);
    const now = Date.now();

    const start = created || now - 30 * 86_400_000;
    const end = Math.max(
      firstPay || 0,
      closing || 0,
      maturity || 0,
      now + 60 * 86_400_000,
      start + 1,
    );
    const range = Math.max(end - start, 1);
    const pct = (t) => {
      if (!t) return null;
      return Math.max(0, Math.min(100, ((t - start) / range) * 100));
    };

    const closingState =
      closing == null
        ? "pending"
        : closing < now
          ? "done"
          : daysFromNow(closing) <= 14
            ? "urgent"
            : "next";
    const firstPayState =
      firstPay == null ? "pending" : firstPay < now ? "done" : "next";
    const maturityState =
      maturity == null ? "pending" : maturity < now ? "done" : "pending";

    let markers = [
      {
        key: "originated",
        label: "Originated",
        dateText: fmtDateShort(created),
        leftPct: pct(created),
        state: "done",
      },
      {
        key: "today",
        label: "Today",
        dateText: fmtDateShort(now),
        leftPct: pct(now),
        state: "today",
      },
      {
        key: "closing",
        label: "Closing",
        dateText: closing
          ? `${fmtDateShort(closing)}${
              daysFromNow(closing) >= 0 ? ` · ${daysFromNow(closing)}d` : ""
            }`
          : null,
        leftPct: pct(closing),
        state: closingState,
      },
      {
        key: "firstPayment",
        label: "First payment",
        dateText: fmtDateShort(firstPay),
        leftPct: pct(firstPay),
        state: firstPayState,
      },
      maturity != null && {
        key: "maturity",
        label: "Maturity",
        dateText: fmtDateShort(maturity),
        leftPct: pct(maturity),
        state: maturityState,
      },
    ]
      .filter(Boolean)
      .filter((m) => m.leftPct !== null);

    // Spread clustered markers — when two milestones land within MIN_GAP%
    // of each other (Originated + Today on a fresh loan, for instance), the
    // labels overlap. Walk left-to-right and nudge each marker right so it
    // sits at least MIN_GAP from the previous one. This distorts time
    // slightly but keeps the visual readable; the date text under each dot
    // still shows the true value.
    const MIN_GAP = 12;
    markers.sort((a, b) => a.leftPct - b.leftPct);
    for (let i = 1; i < markers.length; i += 1) {
      const min = markers[i - 1].leftPct + MIN_GAP;
      if (markers[i].leftPct < min) {
        markers[i] = { ...markers[i], leftPct: Math.min(min, 100) };
      }
    }

    return {
      todayPct: pct(now),
      markers,
    };
  }, [loan]);

  /* Loan contacts — derived from loanRelatedContacts by loanRole. Roles we
     prompt for if missing are listed here so the panel can show empty slots
     with an "Add" button. */
  const contactRows = useMemo(() => {
    // Canonical names from selectOptionsContactRole so matches against
    // loanRelatedContacts[].loanRole actually resolve.
    const REQUIRED_ROLES = [
      "Title Agent",
      "Settlement Agent",
      "Insurance Agent",
      "Buyer Real Estate Agent",
      "Appraiser",
      ...(isConstruction ? ["General Contractor"] : []),
    ];
    const contacts = loan?.loanRelatedContacts || [];
    return REQUIRED_ROLES.map((role) => {
      const match = contacts.find(
        (c) => (c?.loanRole || "").toLowerCase() === role.toLowerCase(),
      );
      if (match) {
        const name =
          `${match.firstName || ""} ${match.lastName || ""}`.trim() ||
          match.emailAddress ||
          "Assigned";
        return {
          key: role,
          role,
          name,
          org: match.company || null,
          missing: false,
        };
      }
      return {
        key: role,
        role,
        name: "Not assigned",
        org: null,
        missing: true,
      };
    });
  }, [loan, isConstruction]);

  /* TRID compliance — only items derivable from loanDocuments[] are checked
     here. Non-tracked items render as permanent "Pending" so the operator
     knows they're a manual handoff. */
  const complianceRows = useMemo(() => {
    const docs = loan?.loanDocuments || [];
    const leDoc = firstDocByType(docs, ["Loan Estimate", "LE"]);
    const cdDoc = firstDocByType(docs, ["Closing Disclosure", "CD"]);
    return [
      {
        key: "le",
        label: "Loan Estimate",
        complete: !!leDoc,
        status: leDoc ? "Received" : "Pending",
      },
      {
        key: "intent",
        label: "Intent to Proceed",
        complete: false,
        status: "Pending",
      },
      {
        key: "cd",
        label: "Closing Disclosure",
        complete: !!cdDoc,
        status: cdDoc ? "Received" : "Pending",
      },
      {
        key: "waiting",
        label: "3-day waiting",
        complete: false,
        status: "Not started",
      },
      {
        key: "rescission",
        label: "Right of rescission",
        complete: false,
        status: "N/A",
      },
      {
        key: "apr",
        label: "APR tolerance",
        complete: false,
        status: "Pending",
      },
    ];
  }, [loan]);

  /* Document checklist — required types per group. Files matching by docType
     are marked complete; the file extension is shown next to the row label. */
  const docCards = useMemo(() => {
    const docs = loan?.loanDocuments || [];
    const toExt = (doc) => {
      const name = doc?.file_display_name || doc?.fileName || "";
      const idx = name.lastIndexOf(".");
      if (idx < 0) return "";
      return name.slice(idx + 1).toLowerCase();
    };
    const buildItems = (entries) =>
      entries.map(({ label, candidates }) => {
        const match = firstDocByType(docs, candidates);
        return {
          key: label,
          label,
          complete: !!match,
          fileExt: match ? toExt(match) : null,
        };
      });

    const cards = [
      {
        title: "Borrower documents",
        items: buildItems([
          {
            label: "Loan application (1003)",
            candidates: ["1003", "1003 Loan Application", "Loan Application"],
          },
          {
            label: "Credit authorization",
            candidates: ["Credit Authorization", "Credit Auth"],
          },
          {
            label: "Government ID",
            candidates: ["Government ID", "Photo ID", "Driver's License"],
          },
          { label: "Bank statements", candidates: ["Bank Statements"] },
          { label: "Tax returns", candidates: ["Tax Returns"] },
          {
            label: "Personal financial statement",
            candidates: ["Personal Financial Statement", "PFS"],
          },
        ]),
      },
      {
        title: "Property documents",
        items: buildItems([
          {
            label: "Purchase contract",
            candidates: ["Purchase Agreement", "Purchase Contract"],
          },
          {
            label: "Appraisal report",
            candidates: ["Appraisal", "Appraisal PDF"],
          },
          { label: "Survey", candidates: ["Survey"] },
          {
            label: "Flood certification",
            candidates: ["Flood Cert", "Flood Certification"],
          },
          { label: "Property photos", candidates: ["Property Photos"] },
          { label: "Property tax bill", candidates: ["Property Tax Bill"] },
        ]),
      },
      {
        title: "Title docs",
        items: buildItems([
          {
            label: "Title commitment",
            candidates: ["Title Commitment", "Title"],
          },
          {
            label: "Title insurance policy",
            candidates: ["Title Insurance"],
          },
          {
            label: "Closing protection letter",
            candidates: ["Closing Protection Letter", "CPL"],
          },
          { label: "Wire instructions", candidates: ["Wire Instructions"] },
          {
            label: "Settlement statement",
            candidates: ["Settlement Statement"],
          },
        ]),
      },
      {
        title: "Insurance docs",
        items: buildItems([
          {
            label: "Hazard insurance",
            candidates: ["Hazard Insurance", "HOI", "Hazard / HOI Binder"],
          },
          {
            label: "Flood insurance",
            candidates: ["Flood Insurance", "Flood Policy"],
          },
          {
            label: "Builder's risk policy",
            candidates: ["Builder's Risk", "Builders Risk"],
          },
        ]),
      },
      ...(isConstruction
        ? [
            {
              title: "Construction documents",
              items: buildItems([
                { label: "Scope of work", candidates: ["Scope of Work"] },
                {
                  label: "Builder contract",
                  candidates: ["Builder Contract"],
                },
                {
                  label: "Plans & specifications",
                  candidates: ["Plans", "Plans / Specs", "Specifications"],
                },
                { label: "Permits", candidates: ["Permits"] },
                { label: "Draw schedule", candidates: ["Draw Schedule"] },
              ]),
            },
          ]
        : []),
      {
        title: "Closing documents",
        items: buildItems([
          { label: "Note", candidates: ["Promissory Note", "Note"] },
          {
            label: "Mortgage / Deed of Trust",
            candidates: ["Mortgage", "Deed of Trust"],
          },
          {
            label: "Closing Disclosure (final)",
            candidates: ["Closing Disclosure", "CD"],
          },
          {
            label: "Disbursement authorization",
            candidates: ["Disbursement Authorization"],
          },
          { label: "Recorded documents", candidates: ["Recorded Documents"] },
        ]),
      },
    ];

    return cards;
  }, [loan, isConstruction]);

  const docTotals = useMemo(() => {
    let total = 0;
    let done = 0;
    docCards.forEach((c) => {
      total += c.items.length;
      done += c.items.filter((i) => i.complete).length;
    });
    return { total, done };
  }, [docCards]);

  const contactsCount = `${contactRows.filter((c) => !c.missing).length} / ${contactRows.length}`;
  const complianceDone = complianceRows.filter((c) => c.complete).length;
  const todayLabel = formatDate(new Date(), "", "MMM d, yyyy").toUpperCase();

  return (
    <Box
      sx={{
        //
        // background: PALETTE.bg,
        // minHeight: "100%",
        // py: 5,
        // px: 6,
        p: 4,
      }}
    >
      <Box sx={{ maxWidth: 1240, mx: "auto" }}>
        <SectionHeader
          title={"Key Dates & Documents"}
          titleSx={{ fontSize: 20, fontWeight: 700 }}
          subTitle={
            "Every milestone, document, and contact this loan needs — pending items stay visible so nothing slips."
          }
        />

        {/* Full-width horizontal activity strip — alternative to the
            stacked LoanActivityCard inside the two-column grid below. */}
        <Box sx={{ mb: "16px" }}>
          <LoanActivityCardHorizontal
            loan={loan}
            lastCommentDate={lastCommentDate}
          />
        </Box>

        {/* TIMELINE */}

        {/* COMBINED TIMELINE — milestones (top) and pipeline stages
            (bottom) sharing one x-axis. The vertical "today" line cuts
            through both tracks so the operator can read across:
            "we're at <stage>, which is <gap> from <milestone>." See
            unifiedTimeline useMemo above for axis math. */}
        <CombinedTimelineCard
          unifiedTimeline={unifiedTimeline}
          milestoneTransitions={milestoneTransitions}
          todayLabel={todayLabel}
        />

        {/* DATE CARDS — split by source so each entity owns its own
            timeline. UpcomingDatesCard / KeyDatesCard removed since their
            rows would all duplicate entries that now live in the
            categorical cards below. */}
        <FlxGrid
          sx={{
            gap: 3,
            mb: "36px",
          }}
        >
          <LoanDatesCard
            loan={loan}
            leadCreatedDate={leadCreatedDate}
            latestMilestoneChange={latestMilestoneChange}
          />

          <BorrowerDatesCard loan={loan} />

          <PropertyDatesCard loan={loan} />
        </FlxGrid>
        <LoanTimelineCard timeline={timeline} todayLabel={todayLabel} />
        {/* DOCUMENT CHECKLIST */}
      </Box>
    </Box>
  );
};

export default LoanDrilldownKeyDatesAndDocumentsTab;
